import React, { useState, useEffect, useRef, useMemo, ChangeEvent } from 'react';
import { Checkbox, Input } from '@forge/common';
import { useTranslation } from 'react-i18next';
import {
  ACTIVITY_FILTER_LIST_MAX_HEIGHT_PX,
  ACTIVITY_FILTER_SEARCH_INPUT_THRESHOLD,
  ACTIVITY_SCROLL_LOAD_THRESHOLD_PX,
  ACTIVITY_SEARCH_DEBOUNCE_MS,
  filterCheckboxOptions,
  getNextVisibleCount,
  isNearBottom,
} from '../../utils';
import { useDebouncedValue } from '../shared/useDebouncedValue';

export interface CheckboxGroupProps {
  groupId: string;
  options: { id: string; label: string }[];
  selected: string[];
  onToggle: (id: string) => void;
  onSelectAll: (targetIds?: string[]) => void;
  enableInfiniteScroll?: boolean;
  initialPageSize?: number;
}

const ActivityFilterCheckboxGroup: React.FC<CheckboxGroupProps> = React.memo(({
  groupId,
  options,
  selected,
  onToggle,
  onSelectAll,
  enableInfiniteScroll = false,
  initialPageSize = 10,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [visibleCount, setVisibleCount] = useState(initialPageSize);
  const debouncedSearchTerm = useDebouncedValue(searchTerm, ACTIVITY_SEARCH_DEBOUNCE_MS);
  const effectiveSearchTerm = searchTerm === '' ? '' : debouncedSearchTerm;
  const containerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  // Filter options by search term
  const filtered = useMemo(
    () => filterCheckboxOptions(options, effectiveSearchTerm),
    [options, effectiveSearchTerm]
  );

  // Visible items
  const visible = useMemo(() => filtered.slice(0, visibleCount), [filtered, visibleCount]);
  const hasMore = visibleCount < filtered.length;

  // Auto-load on scroll
  useEffect(() => {
    if (!enableInfiniteScroll || !hasMore || !containerRef.current) return;

    const container = containerRef.current;
    const handleScroll = () => {
      if (isNearBottom(container.scrollTop, container.clientHeight, container.scrollHeight, ACTIVITY_SCROLL_LOAD_THRESHOLD_PX)) {
        setVisibleCount(prev => getNextVisibleCount(prev, initialPageSize, filtered.length));
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [enableInfiniteScroll, hasMore, initialPageSize, filtered.length]);

  // Reset pagination on search
  useEffect(() => {
    setVisibleCount(initialPageSize);
  }, [effectiveSearchTerm, initialPageSize]);

  // Check selection state
  const selectedSet = useMemo(() => new Set(selected), [selected]);
  // Select-all semantics are based on filtered items (not just visible slice)
  // so search + infinite scroll behaves predictably.
  const allSelected = filtered.length > 0 && filtered.every(opt => selectedSet.has(opt.id));
  const someSelected = filtered.some(opt => selectedSet.has(opt.id)) && !allSelected;
  const shouldShowSearch = options.length > ACTIVITY_FILTER_SEARCH_INPUT_THRESHOLD;
  const hasSearchQuery = effectiveSearchTerm?.trim()?.length > 0;

  if (options.length === 0) {
    return (
      <p className="tw-text-sm activity-text-secondary tw-px-4 tw-py-2">
        {t('activityLogFilterNoOptions')}
      </p>
    );
  }

  return (
    <div className="tw-flex tw-flex-col tw-gap-3">
      {/* Search with built-in debounce */}
      {shouldShowSearch && (
        <div className="tw-px-4 tw-pt-2">
          <Input
            id={`${groupId}-search`}
            placeholder={t('search') || 'Search...'}
            value={searchTerm}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
          />
        </div>
      )}
      {/* Scrollable List */}
      <div
        ref={containerRef}
        className="tw-px-4"
        style={{ maxHeight: ACTIVITY_FILTER_LIST_MAX_HEIGHT_PX, overflowY: enableInfiniteScroll ? 'auto' : 'visible' }}
      >
        <ul className="tw-list-none tw-p-0 tw-m-0 tw-flex tw-flex-col tw-gap-2 tw-pb-1">
          <li className="tw-pt-2">
            <Checkbox
              id={`${groupId}-select-all`}
              checked={allSelected}
              indeterminate={someSelected}
              onChange={() => onSelectAll(filtered.map(opt => opt.id))}
            >
              {t('selectAll')}
            </Checkbox>
          </li>
          <li><hr className="activity-filter-select-all-divider" /></li>
          {visible.map(opt => (
            <li key={opt.id}>
              <Checkbox
                id={`${groupId}-${opt.id}`}
                checked={selectedSet.has(opt.id)}
                onChange={() => onToggle(opt.id)}
              >
                {opt.label}
              </Checkbox>
            </li>
          ))}
          {/* No Results */}
          {filtered.length === 0 && hasSearchQuery && (
            <li>
              <p className="tw-text-sm activity-text-secondary tw-py-2">
                {t('noResults')}
              </p>
            </li>
          )}
        </ul>
      </div>
    </div>
  );
});

export default ActivityFilterCheckboxGroup;
